import { lazy, Suspense, useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { PageLoadingState } from '@/components/ui/LoadingSpinner';
import { Button } from '@/components/ui/Button';
import { LoginScreen } from '@/components/auth/LoginScreen';
import { MfaCodeStep } from '@/components/auth/MfaCodeStep';
import { useAuth } from '@/contexts/AuthContext';
import { useExternalServiceProvider } from '@/hooks/useExternalServiceProvider';
import { useLogin, useResendMfaCode, useVerifyMfaLogin } from '@/hooks/useAuth';
import { AUTH } from '@/constants/auth';
import { canRenderInFrame } from '@/lib/framePolicy';
import { ForbiddenQueryRedirectListener } from './ForbiddenQueryRedirectListener';
import { BulkSelectionForwardNavigationListener } from '@/components/routing/BulkSelectionForwardNavigationListener';

const MainAppRoutes = lazy(() => import('@/routes/MainAppRoutes'));
const ExternalServiceProviderRoutes = lazy(() => import('@/routes/ExternalServiceProviderRoutes'));
const GuestForgotPasswordRoute = lazy(() =>
  import('./auth/GuestForgotPasswordRoute').then((m) => ({ default: m.GuestForgotPasswordRoute })),
);
const GuestRegisterRoute = lazy(() =>
  import('./auth/GuestRegisterRoute').then((m) => ({ default: m.GuestRegisterRoute })),
);
const GuestRegisterWithCodeRoute = lazy(() =>
  import('./auth/GuestRegisterWithCodeRoute').then((m) => ({ default: m.GuestRegisterWithCodeRoute })),
);

const PublicResidentLoginForm = lazy(() => import('@/pages/public/PublicResidentLoginForm'));
const PublicLandlordLoginForm = lazy(() => import('@/pages/public/PublicLandlordLoginForm'));
const PublicServiceRequestTracking = lazy(() => import('@/pages/public/PublicServiceRequestTracking'));
const PublicServiceRequestForm = lazy(() => import('@/pages/public/PublicServiceRequestForm'));
const ResidentMagicLinkVerify = lazy(() => import('@/pages/public/ResidentMagicLinkVerify'));
const LandlordMagicLinkVerify = lazy(() => import('@/pages/public/LandlordMagicLinkVerify'));
const ResidentPortal = lazy(() => import('@/pages/portal/ResidentPortal'));
const LandlordPortal = lazy(() => import('@/pages/portal/LandlordPortal'));
const ResidentPortalServiceRequestDetail = lazy(() => import('@/pages/portal/ResidentPortalServiceRequestDetail'));
const LandlordPortalServiceRequestDetail = lazy(() => import('@/pages/portal/LandlordPortalServiceRequestDetail'));
const PrivacyPolicy = lazy(() => import('@/pages/public/PrivacyPolicy'));
const Changelog = lazy(() => import('@/pages/Changelog'));
const GuideArticle = lazy(() => import('@/pages/Guide').then((m) => ({ default: m.GuideArticle })));
const GuideIndex = lazy(() => import('@/pages/Guide').then((m) => ({ default: m.GuideIndex })));
const PublicTrackedAsset = lazy(() => import('@/pages/PublicTrackedAsset'));
const PublicRentalListing = lazy(() => import('@/pages/PublicRentalListing'));
const PublicCompanyRentalListings = lazy(() => import('@/pages/PublicCompanyRentalListings'));

export { PrivacyPolicy, Changelog, GuideArticle, GuideIndex };

/** Shown while the session probe runs. After a few seconds (typical Cloud Run cold start) we
 *  clarify that the server may be waking up; on exhausted retries we offer a manual retry. */
export function FullPageLoading({
  showRetry = false,
  onRetry,
}: {
  showRetry?: boolean;
  onRetry?: () => void;
} = {}) {
  const { t } = useTranslation();
  const [showWakeHint, setShowWakeHint] = useState(false);

  useEffect(() => {
    if (showRetry) return;
    const timer = window.setTimeout(() => setShowWakeHint(true), 5000);
    return () => window.clearTimeout(timer);
  }, [showRetry]);

  return (
    <div className='min-h-screen bg-dark dark:bg-gray-900 text-white flex items-center justify-center'>
      <div className='text-center max-w-sm px-4'>
        <img src='/xkey-clean.png' alt='Xkey logo' height='50' width='108' className='mx-auto mb-4 animate-pulse' />
        <p className='text-white'>{showRetry ? t('errors.timeoutError') : t('actions.loading')}</p>
        {(showWakeHint || showRetry) && (
          <p className='text-sm text-white/70 mt-2'>
            {showRetry ? t('errors.timeoutErrorMessage') : t('actions.serverStarting')}
          </p>
        )}
        {showRetry && onRetry && (
          <Button type='button' variant='secondary' className='mt-4' onClick={onRetry}>
            {t('errors.retry')}
          </Button>
        )}
      </div>
    </div>
  );
}

/** Logged-in main app, ESP dashboard, or login shell — everything not matched above as a public/guest-auth route. */
function PrivateAppShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const invitationCodeFromState = (location.state as { invitationCode?: string } | null)?.invitationCode;
  const { isAuthenticated, isLoading, isBootstrapError, retryBootstrap } = useAuth();
  const { isExternalServiceProvider } = useExternalServiceProvider();

  const loginMutation = useLogin();
  const verifyMfaMutation = useVerifyMfaLogin();
  const resendMfaMutation = useResendMfaCode();

  const [mfa, setMfa] = useState<null | {
    mfaToken: string;
    email: string;
    methods: Array<'email' | 'totp' | 'recovery'>;
    preferred?: 'email' | 'totp';
  }>(null);

  const navigateAfterLogin = useCallback(() => {
    const storedCode = sessionStorage.getItem(AUTH.PENDING_INVITATION_CODE_KEY);
    const code = invitationCodeFromState ?? storedCode ?? undefined;
    if (storedCode) {
      sessionStorage.removeItem(AUTH.PENDING_INVITATION_CODE_KEY);
    }
    if (code) {
      navigate(`/rekisteroidy/${code}`, { replace: true });
    }
  }, [invitationCodeFromState, navigate]);

  if (isLoading || isBootstrapError) {
    return <FullPageLoading showRetry={isBootstrapError} onRetry={retryBootstrap} />;
  }

  if (!isAuthenticated) {
    if (mfa) {
      return (
        <MfaCodeStep
          email={mfa.email}
          methods={mfa.methods}
          preferred={mfa.preferred}
          isSubmitting={verifyMfaMutation.isPending}
          isResending={resendMfaMutation.isPending}
          onBack={() => setMfa(null)}
          onResend={async () => {
            const result = await resendMfaMutation.mutateAsync(mfa.mfaToken);
            setMfa((prev) => (prev ? { ...prev, mfaToken: result.mfaToken } : null));
          }}
          onSubmit={async ({ code, method }) => {
            await verifyMfaMutation.mutateAsync({ mfaToken: mfa.mfaToken, code, method });
            setMfa(null);
            navigateAfterLogin();
          }}
        />
      );
    }

    return (
      <Suspense fallback={null}>
        <Routes>
          <Route
            path='*'
            element={
              <LoginScreen
                isLoading={loginMutation.isPending || verifyMfaMutation.isPending || resendMfaMutation.isPending}
                onLogin={async ({ email, password }) => {
                  try {
                    const result = await loginMutation.mutateAsync({ email, password });
                    if (result.step === 'mfa') {
                      setMfa({
                        mfaToken: result.mfaToken,
                        email: result.email,
                        methods: result.methods,
                        preferred: result.preferred,
                      });
                      return;
                    }
                    navigateAfterLogin();
                  } catch {
                    // Wrong password etc. surfaced via mutation onError (toast); avoid unhandled rejections.
                  }
                }}
                onForgotPassword={() => {
                  navigate('/unohdin-salasanani');
                }}
              />
            }
          />
        </Routes>
      </Suspense>
    );
  }

  if (isExternalServiceProvider) {
    return (
      <>
        <Suspense fallback={<PageLoadingState />}>
          <ExternalServiceProviderRoutes />
        </Suspense>
        <ForbiddenQueryRedirectListener />
        <BulkSelectionForwardNavigationListener />
      </>
    );
  }

  return (
    <>
      <Suspense fallback={<PageLoadingState />}>
        <MainAppRoutes />
      </Suspense>
      <ForbiddenQueryRedirectListener />
      <BulkSelectionForwardNavigationListener />
    </>
  );
}

/** Single route tree: public pages, guest-only auth screens, then authenticated app behind `PrivateAppShell`. */
export function ApplicationRoutes() {
  const { pathname } = useLocation();
  // Recheck every client-side navigation: the response's frame-ancestors policy
  // does not change when React Router moves away from the embedded form.
  if (!canRenderInFrame(pathname, window.self !== window.top)) return null;

  return (
    <Suspense fallback={<PageLoadingState />}>
      <Routes>
        <>
          {/* Privacy, changelog, embedded guide (no auth shell) */}
          <Route path='/tietosuojaseloste' element={<PrivacyPolicy />} />
          <Route path='/muutosloki' element={<Changelog />} />
          <Route path='/ohjeet' element={<GuideIndex />} />
          <Route path='/ohjeet/:slug' element={<GuideArticle />} />
        </>
        <>
          {/* Resident: magic links, portal, public login — specific paths before /asukas/:slug */}
          <Route path='/asukas/kirjaudu/:token' element={<ResidentMagicLinkVerify />} />
          <Route path='/asukas/kirjaudu' element={<ResidentMagicLinkVerify />} />
          <Route path='/asukas/palvelupyynnot/:id' element={<ResidentPortalServiceRequestDetail />} />
          <Route path='/asukas' element={<ResidentPortal />} />
          <Route path='/asukas/:slug' element={<PublicResidentLoginForm />} />
        </>
        <>
          {/* Landlord: same pattern as resident */}
          <Route path='/vuokranantaja/kirjaudu/:token' element={<LandlordMagicLinkVerify />} />
          <Route path='/vuokranantaja/kirjaudu' element={<LandlordMagicLinkVerify />} />
          <Route path='/vuokranantaja/palvelupyynnot/:id' element={<LandlordPortalServiceRequestDetail />} />
          <Route path='/vuokranantaja' element={<LandlordPortal />} />
          <Route path='/vuokranantaja/:slug' element={<PublicLandlordLoginForm />} />
        </>
        <>
          {/* Anonymous service-request form, embed, and tracking — specific paths before :slug */}
          <Route path='/palvelupyynto/seuraa/:accessToken' element={<PublicServiceRequestTracking />} />
          <Route path='/palvelupyynto/upotus/:slug' element={<PublicServiceRequestForm embedded />} />
          <Route path='/palvelupyynto/:slug' element={<PublicServiceRequestForm />} />
          <Route path='/omaisuus/:token' element={<PublicTrackedAsset />} />
          <Route path='/vuokraus/yhtio/:slug' element={<PublicCompanyRentalListings />} />
          <Route path='/vuokraus/:listingToken' element={<PublicRentalListing />} />
        </>
        <>
          {/* Guest-only auth (handled before PrivateAppShell for known paths) */}
          <Route path='/kayttoehdot' element={<Navigate to='/tietosuojaseloste' replace />} />
          <Route path='/rekisteroidy' element={<GuestRegisterRoute />} />
          <Route path='/rekisteroidy/:code' element={<GuestRegisterWithCodeRoute />} />
          <Route path='/unohdin-salasanani' element={<GuestForgotPasswordRoute />} />
          <Route path='/unohdin-salasanani/:code' element={<GuestForgotPasswordRoute />} />
        </>
        <Route path='*' element={<PrivateAppShell />} />
      </Routes>
    </Suspense>
  );
}
